import { FC, useEffect, useState } from 'react';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ButtonType } from 'components/custom-button/CustomButton';
import { CustomButton } from 'components';
import { CircleCloseIcon } from 'assets/icons';
import { CustomModal } from 'components';
import SortableItem from './SortableItem';

export interface ColumnConfig {
  columnKey: string;
  columnLabel: string;
  isEnabled: boolean;
  priority: number;
  showAlways: boolean;
}

interface CustomizeColumnsModalProps {
  open: boolean;
  onClose: () => void;
  columns: ColumnConfig[];
  onSave: (columns: ColumnConfig[]) => void;
  reorderableStartIndex?: number;
  isLoading?: boolean;
}

const CustomizeColumnsModal: FC<CustomizeColumnsModalProps> = ({
  open,
  onClose,
  columns,
  onSave,
  reorderableStartIndex = 0,
  isLoading = false
}) => {
  const [items, setItems] = useState<ColumnConfig[]>(columns);

  useEffect(() => {
    setItems(columns);
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems(prevItems => {
        const oldIndex = prevItems.findIndex(item => item.columnKey === active.id);
        const newIndex = prevItems.findIndex(item => item.columnKey === over.id);

        // Prevent dragging to positions before reorderableStartIndex
        if (newIndex < reorderableStartIndex) {
          return prevItems;
        }

        return arrayMove(prevItems, oldIndex, newIndex);
      });
    }
  };

  const toggleColumnSelection = (columnKey: string) => {
    setItems(prevItems => {
      return prevItems.map(item => (item.columnKey === columnKey ? { ...item, isEnabled: !item.isEnabled } : item));
    });
  };

  const handleSave = () => {
    const itemsWithPriorityOrder = items.map((item, index) => ({
      ...item,
      priority: index + 1
    }));

    onSave(itemsWithPriorityOrder);
  };

  const isItemReorderable = (index: number) => index >= reorderableStartIndex;

  return (
    <CustomModal open={open} closeModal={onClose} width="md:w-[488px]">
      <div className="flex h-full flex-col gap-7 px-[30px] pt-4 pb-6">
        <div className="relative flex items-center">
          <div className="text-xl font-bold text-heading">Edit table</div>
          <CircleCloseIcon className="absolute right-0 top-0 cursor-pointer" onClick={onClose} />
        </div>
        <div className="flex flex-col gap-6">
          <div
            className="customNormalScroll relative max-h-[60vh] overflow-y-auto overflow-x-clip"
            id="columns-container">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToParentElement, restrictToVerticalAxis]}>
              <SortableContext items={items.map(item => item.columnKey)} strategy={verticalListSortingStrategy}>
                {items.map((column, index) => {
                  const canReorder = isItemReorderable(index);
                  return (
                    <SortableItem
                      key={column.columnKey}
                      column={column}
                      toggleColumnSelection={toggleColumnSelection}
                      canReorder={canReorder}
                      isFirst={index === 0}
                      isLast={index === items.length - 1}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
          <div className="flex justify-end gap-4">
            <CustomButton
              id="cancel-columns-button"
              onClick={onClose}
              type={ButtonType.SECONDARY}
              className="h-10 w-[100px]">
              Cancel
            </CustomButton>
            <CustomButton
              id="save-columns-button"
              onClick={handleSave}
              type={ButtonType.PRIMARY}
              className="h-10 w-[100px]"
              isLoading={isLoading}>
              Save
            </CustomButton>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default CustomizeColumnsModal;
